import { Container, Sprite } from 'pixi.js';
import { EventEmitter, TextureFactory } from '../core';
import { Point, Vector } from '../interface';

class HealthPointIcon {
  public readonly container: Container;
  private readonly sprite: Sprite;
  public readonly epsilon: number;
  public damaged: boolean = false;
  public emitter: EventEmitter;
  public position: Point;
  public velocity: Vector;
  public rotationVelocity: number = (Math.random() * Math.PI) / 10 - (Math.random() * Math.PI) / 10;

  private vibrateStrength: number;
  private vibrateRadian: number;
  private vibrateReducingCoefficient: number;
  private vibratePeriod: number;
  private currentVibratePeriod: number;
  private rgb: [number, number, number];
  private scaleRadian: number;

  constructor() {
    this.container = new Container();
    this.vibrateReducingCoefficient = 0.85;
    this.emitter = new EventEmitter();
    this.position = { x: 0, y: 0 };
    this.velocity = { x: 0, y: 0 };
    this.vibrateStrength = 0;
    this.vibrateRadian = 0;
    this.vibratePeriod = 1;
    this.currentVibratePeriod = 0;
    this.epsilon = 0.001;
    this.rgb = [255, 255, 255];
    this.scaleRadian = Math.random() * Math.PI;

    const heart = TextureFactory.instance.getSingleTexture('/textures/ui/heart.png');

    this.sprite = new Sprite(heart);
    this.sprite.anchor.set(0.5);
    this.sprite.scale.set(1);
    this.sprite.alpha = 0;

    this.container.addChild(this.sprite);
  }

  public update(elapsed: number): void {
    this.position.x += this.velocity.x * elapsed;
    this.position.y += this.velocity.y * elapsed;
    this.scaleRadian += (elapsed * Math.PI) / 120;
    this.scaleRadian = this.scaleRadian % Math.PI;

    this.applyPosition();
    this.applyVibration();
    this.reduceVibrate(elapsed);

    if (this.damaged) {
      if (this.rgb[0] >= 8 * elapsed) {
        this.rgb[0] = this.rgb[1] = this.rgb[2] = Math.floor(this.rgb[0] - 8 * elapsed);
      }
      if (this.rgb[0] < 0) {
        this.rgb[0] = this.rgb[1] = this.rgb[2] = 0;
      }
    } else {
      this.container.scale.set(0.5 + Math.sin(this.scaleRadian) / 20);
    }
    this.sprite.tint = this.tint;

    if (this.damaged && this.vibrateStrength <= 1) {
      this.container.rotation += this.rotationVelocity;
      if (this.velocity.y < 100) {
        this.velocity.y += 1.5 * elapsed;
      }
      if (this.container.scale.x > 0) {
        this.container.scale.set(this.container.scale.x - 0.02 * elapsed);
      }
      if (this.container.alpha > 0) {
        this.container.alpha -= 0.03 * elapsed;
      } else {
        this.emitter.emit('removed');
      }
    } else {
      this.sprite.scale.x += (1 - this.sprite.scale.x) * 0.05 * elapsed;
      this.sprite.scale.y += (1 - this.sprite.scale.y) * 0.05 * elapsed;
      this.sprite.alpha += (1 - this.sprite.alpha) * 0.05 * elapsed;
    }
  }

  private get tint(): number {
    return (this.rgb[0] << 16) + (this.rgb[1] << 8) + this.rgb[2];
  }

  public damage(): void {
    this.damaged = true;
    this.vibrate(10);
  }

  public vibrate(strength: number, degree = Math.random() * 360): void {
    this.vibrateStrength = strength;
    this.vibrateRadian = (degree / 180) * Math.PI;
  }

  private applyPosition(): void {
    if (!this.container) return;

    this.container.x = this.position.x;
    this.container.y = this.position.y;
  }

  private applyVibration(): void {
    if (!this.container) return;

    this.container.x += Math.cos(this.vibrateRadian) * this.vibrateStrength;
    this.container.y += Math.sin(this.vibrateRadian) * this.vibrateStrength;
  }

  private reduceVibrate(elapsed: number): void {
    this.currentVibratePeriod += elapsed;

    if (this.currentVibratePeriod >= this.vibratePeriod) {
      if (Math.abs(this.vibrateStrength) <= this.epsilon) {
        this.vibrateStrength = 0;
      } else {
        this.vibrateStrength = this.vibrateStrength * this.vibrateReducingCoefficient;
        this.vibrateRadian += Math.PI;
      }

      this.currentVibratePeriod -= this.vibratePeriod;
    }

    if (this.currentVibratePeriod < 0) {
      this.currentVibratePeriod = 0;
    }
  }
}

class DrawingGameHealth extends Container {
  private aliveHealthIcons: Array<HealthPointIcon> = [];
  private deadHeathIcons: Array<HealthPointIcon> = [];

  public update(elapsed: number): void {
    for (let healthIcon of this.aliveHealthIcons) {
      healthIcon.update(elapsed);
    }
    for (let healthIcon of this.deadHeathIcons) {
      healthIcon.update(elapsed);
    }
  }

  public setHP(hp: number): void {
    for (let i = this.aliveHealthIcons.length; i < hp; i++) {
      const healthIcon = new HealthPointIcon();

      healthIcon.position.x = this.aliveHealthIcons.reduce((acc) => acc + 37, 18);
      healthIcon.position.y = 18;

      this.aliveHealthIcons.push(healthIcon);
      this.addChild(healthIcon.container);

      healthIcon.emitter.on('removed', () => {
        this.deadHeathIcons.splice(this.deadHeathIcons.indexOf(healthIcon), 1);
        this.removeChild(healthIcon.container);
        healthIcon.container.destroy();
      });
    }

    for (let i = this.aliveHealthIcons.length - 1; i >= hp; i--) {
      const healthIcon = this.aliveHealthIcons.pop();

      if (healthIcon) {
        healthIcon.damage();
        this.deadHeathIcons.push(healthIcon);
      }
    }
  }
}

export default DrawingGameHealth;
